import { useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { BidContractType, BidScopeSource, SaveBidPackageInput } from "src/generated/graphqlTypes";
import {
  PlanBasedBidPackagePage_BidPackageFragment,
  usePreviewSavePlanBasedBidPackageLazyQuery,
  useSavePlanBasedBidPackagesMutation,
} from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import {
  INTERNAL_ESTIMATE_ID,
  mapToBidPlanGroupsInput,
  PlanBasedBidPackageFormObjectState,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { createPlanBasedBidPackageEditUrl } from "src/RouteUrls";
import { StringParam, useQueryParams } from "use-query-params";

/**
 * The single write path for the plan-based bid wizard's form. `saveBidPackage`'s `bidPlanGroups` input is exhaustive,
 * so anything that persists groups behind this form's back leaves it stale.
 */
export function useSavePlanBasedBidPackage(formState: PlanBasedBidPackageFormObjectState) {
  const [saveBidPackages, { loading: saving }] = useSavePlanBasedBidPackagesMutation();
  // A dry run against the current server state, so never answer from a cached result
  const [previewSave, { loading: previewing }] = usePreviewSavePlanBasedBidPackageLazyQuery({
    fetchPolicy: "no-cache",
  });
  const { openModal } = useModal();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });

  const persist = useCallback(async () => {
    const result = await saveBidPackages({ variables: { input: [mapToSaveBidPackagesInput(formState)] } });
    const saved = result.data?.saveBidPackages.bidPackages?.first;
    const groupId = saved?.bidPackageGroup?.id;
    if (saved && groupId) {
      // Fold the saved server response back into the form. `update` re-runs the form's `init.map`
      // (mapBidPackageToForm) and reconciles field-by-field, so it both picks up the server-assigned
      // ids and marks the form clean (this also commits form changes so the stepper won't prompt about
      // unsaved changes).
      formState.update(saved);
      window.history.replaceState(null, "", createPlanBasedBidPackageEditUrl(groupId, projectId));
    }
    return saved;
  }, [formState, saveBidPackages, projectId]);

  /**
   * `persist`, but first dry runs the save and, if it would remove bid lines that already have costs, asks the user to
   * confirm. Resolves with the saved package, or `undefined` when the user cancels (nothing is persisted).
   */
  const persistWithCostRemovalConfirmation = useCallback(async () => {
    const input = mapToSaveBidPackagesInput(formState);
    // A new package has no lines to lose; only bid plan group edits can soft-delete costed lines
    if (!input.id || !formState.bidPlanGroups.dirty) return persist();
    const { data } = await previewSave({ variables: { input } });
    if (!data?.previewSaveBidPackage.removedCostedLines.nonEmpty) return persist();
    return new Promise<PlanBasedBidPackagePage_BidPackageFragment | undefined>((resolve) => {
      let saved: PlanBasedBidPackagePage_BidPackageFragment | undefined;
      openModal({
        content: (
          <ConfirmationModal
            danger
            title="Remove Bid Lines With Costs?"
            label="Yes"
            confirmationMessage={
              `The changes you have made will remove ` +
              `${data?.previewSaveBidPackage.removedCostedLines.length} bid lines that currently have costs on them. ` +
              `Are you sure you want to make these changes?`
            }
            onConfirmAction={async () => {
              saved = await persist();
            }}
          />
        ),
        // Fires on Cancel and after a confirmed save (the modal closes itself), so callers resume either way
        onClose: () => resolve(saved),
      });
    });
  }, [formState, previewSave, openModal, persist]);

  return { persist, persistWithCostRemovalConfirmation, saveBidPackages, saving: saving || previewing };
}

function mapToSaveBidPackagesInput(formState: ObjectState<PlanBasedBidPackageFormState>): SaveBidPackageInput {
  const {
    bidPackageId,
    bidPackageGroupId,
    marketId,
    projects,
    costCodes,
    costType,
    internalEstimateType,
    productOfferings,
    requests,
    bidPlanGroups,
  } = formState.value;
  const internalEstimate = requests?.find((r) => r.tradePartnerId === INTERNAL_ESTIMATE_ID);
  return {
    id: bidPackageId ?? undefined,
    bidPackageGroupId: bidPackageGroupId ?? undefined,
    projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
    costCodes: costCodes ?? [],
    costType: costType ?? undefined,
    scopeSource: BidScopeSource.ProductOffering,
    isUnitBased: false,
    isPlanBasedV2: true,
    internalEstimateBidContract: internalEstimate
      ? {
          id: internalEstimate.id,
          isInternalEstimate: true,
          internalEstimateType,
          archive: internalEstimate.archive,
          type: BidContractType.UserCreatedGroupBased,
        }
      : undefined,
    productOfferings: productOfferings ?? undefined,
    requests: requests?.nonEmpty ? requests.filter((r) => r.tradePartnerId !== INTERNAL_ESTIMATE_ID) : undefined,
    bidPlanGroups: bidPlanGroups ? mapToBidPlanGroupsInput(bidPlanGroups) : undefined,
  };
}
